import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown } from 'lucide-react'
import { Button } from '../ui/button'
import { Label } from '../ui/label'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { DurationWheels } from './DurationField'
import { SliderField } from './SliderField'
import { formatSeconds } from './custom-exercise-form'
import { cn } from '../../lib/utils'

interface CountAndRestFieldProps {
  /** What is being counted, e.g. "Sets". */
  countLabel: string
  /** The rest that separates them, e.g. "Rest between sets". */
  restLabel: string
  count: number | null
  restSeconds: number | null
  /** Highest count the dial goes to — 30 sets, 30 reps. */
  max: number
  onChange: (next: { count: number; restSeconds: number }) => void
  invalid?: boolean
  id?: string
}

/**
 * How many, and how long between them — the pair the timer actually needs, set
 * together in one dialog.
 *
 * The two live on the same control because a rest only exists between things:
 * with a count of one there is nothing for it to separate, and the wheels stay
 * out of the way until the count says otherwise. Used twice per block, for
 * sets/rest-between-sets and for reps/rest-between-reps, which are the same
 * question asked at two scales.
 */
export function CountAndRestField({
  countLabel,
  restLabel,
  count,
  restSeconds,
  max,
  onChange,
  invalid = false,
  id,
}: CountAndRestFieldProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [draftCount, setDraftCount] = useState(1)
  const [draftRest, setDraftRest] = useState(0)

  // Every opening starts a fresh draft off what is saved, so a dismissed dialog
  // decides nothing.
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setDraftCount(count ?? 1)
      setDraftRest(restSeconds ?? 0)
    }
  }

  function confirm() {
    // A rest kept from a previous edit would be invisible at a count of one and
    // would come back the moment the count went up, so it is dropped here.
    onChange({ count: draftCount, restSeconds: draftCount > 1 ? draftRest : 0 })
    setOpen(false)
  }

  const summary = (() => {
    if (count == null) return t('common.notSet', '—')
    const counted = t('trainingDiary.customExercise.blocks.countValue', '{{count}}×', { count })
    if (count > 1 && restSeconds != null && restSeconds > 0) {
      return `${counted} · ${formatSeconds(restSeconds)}`
    }
    return counted
  })()

  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-xs" htmlFor={id}>
        {countLabel}
      </Label>
      <Button
        type="button"
        variant="outline"
        id={id}
        onClick={() => setOpen(true)}
        aria-label={countLabel}
        aria-invalid={invalid || undefined}
        className={cn(
          'h-9 w-full justify-between font-normal',
          count == null && 'text-muted-foreground',
          invalid && 'border-destructive text-destructive'
        )}
      >
        {summary}
        <ChevronDown className="size-4 opacity-50" aria-hidden />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-sm" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>{countLabel}</DialogTitle>
          </DialogHeader>

          <SliderField
            label={countLabel}
            value={draftCount}
            onChange={(next) => setDraftCount(next ?? 1)}
            min={1}
            max={max}
          />

          {draftCount > 1 && (
            <div className="flex flex-col gap-2">
              <Label className="text-xs">{restLabel}</Label>
              <div className="flex justify-center">
                <DurationWheels seconds={draftRest} onChange={setDraftRest} />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button type="button" onClick={confirm}>
              {t('common.save', 'Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
